import { useCallback, useEffect, useRef, useState } from 'react'
import type { AiResultaat, AiSoort, AiStand } from '../../gedeeld/types'
import { api } from './api'

/* ===========================================================================
 *  De eigen AI vragen, en wachten
 *
 *  Casper: "geen claude api gebruiken, alles via onze locale ollama" -- sinds
 *  6 oktober 2026 is dat LM Studio, achter dezelfde lezer; hier verandert
 *  daar niets door.
 *
 *  De vraag gaat naar de pc op kantoor (ai_opdrachten, 0051), en die doet er
 *  zo lang over als hij erover doet -- een paar seconden voor een korte
 *  vraag, een paar minuten voor een plan van twintig taken.
 *
 *  Dus: neerleggen, en dan om de paar seconden vragen hoe het staat. Het
 *  scherm laat zien wat er gebeurt -- "er staan 2 vragen vóór je", "de pc is
 *  bezig", "de pc op kantoor staat uit" -- want een draaiend rondje zonder
 *  uitleg is na twintig seconden een rondje waarvan iemand denkt dat het
 *  vastzit.
 *
 *  Het antwoord blijft ook bewaard als je weggaat (0156 §13): wie het
 *  tabblad dichtdoet en later terugkomt in een gesprek, ziet het antwoord
 *  alsnog staan.
 * =========================================================================== */

export interface AiToestand {
  bezig: boolean
  stand: AiStand | null
  resultaat: AiResultaat | null
  fout: string | null
  opdracht: string | null
  gesprekId: string | null
}

const LEEG: AiToestand = { bezig: false, stand: null, resultaat: null, fout: null, opdracht: null, gesprekId: null }

/** Hoe lang tussen twee keer vragen: eerst vlot, daarna rustiger. */
const tussenpoos = (keer: number) => (keer < 10 ? 1500 : keer < 40 ? 3000 : 6000)

export function useAi(bijKlaar?: (r: AiResultaat, stand: AiStand) => void) {
  const [t, setT] = useState<AiToestand>(LEEG)
  const levend = useRef(true)
  const klok = useRef<ReturnType<typeof setTimeout> | null>(null)
  const klaarRef = useRef(bijKlaar)
  klaarRef.current = bijKlaar

  useEffect(() => {
    levend.current = true
    return () => {
      levend.current = false
      if (klok.current) clearTimeout(klok.current)
    }
  }, [])

  const volg = useCallback((opdracht: string, keer = 0) => {
    klok.current = setTimeout(async () => {
      if (!levend.current) return
      try {
        const stand = await api.aiStand(opdracht)
        if (!levend.current) return
        if (stand.status === 'klaar') {
          const r = stand.resultaat ?? {}
          setT((x) => ({ ...x, bezig: false, stand, resultaat: r }))
          klaarRef.current?.(r, stand)
          return
        }
        if (stand.status === 'mislukt') {
          setT((x) => ({ ...x, bezig: false, stand, fout: stand.fout ?? 'De eigen AI kwam er niet uit.' }))
          return
        }
        setT((x) => ({ ...x, stand }))
        volg(opdracht, keer + 1)
      } catch (e) {
        if (!levend.current) return
        /* Eén keer geen antwoord van de server is geen mislukte vraag: de
           pc rekent misschien gewoon door. Rustig opnieuw proberen. */
        if (keer < 200) volg(opdracht, keer + 1)
        else setT((x) => ({ ...x, bezig: false, fout: e instanceof Error ? e.message : String(e) }))
      }
    }, tussenpoos(keer))
  }, [])

  const vraag = useCallback(async (soort: AiSoort, lijf: Record<string, unknown>) => {
    if (klok.current) clearTimeout(klok.current)
    setT({ ...LEEG, bezig: true })
    try {
      const uit = await api.aiStart(soort, lijf)
      if (!levend.current) return null
      setT((x) => ({
        ...x,
        opdracht: uit.opdracht,
        gesprekId: uit.gesprekId ?? null,
        stand: { status: 'wacht', online: uit.online },
      }))
      volg(uit.opdracht)
      return uit
    } catch (e) {
      setT({ ...LEEG, fout: e instanceof Error ? e.message : String(e) })
      return null
    }
  }, [volg])

  /** Verder volgen met een vraag die al liep, bijvoorbeeld na terugkomen. */
  const hervat = useCallback((opdracht: string) => {
    if (klok.current) clearTimeout(klok.current)
    setT({ ...LEEG, bezig: true, opdracht, stand: { status: 'wacht' } })
    volg(opdracht, 0)
  }, [volg])

  const wis = useCallback(() => {
    if (klok.current) clearTimeout(klok.current)
    setT(LEEG)
  }, [])

  return { ...t, vraag, hervat, wis }
}

/** Wat er bij een wachtende vraag op het scherm hoort. */
export function standTekst(s: AiStand | null): string {
  if (!s) return 'Even geduld…'
  if (s.status === 'bezig') return 'De eigen AI is ermee bezig…'
  if (s.online === false) {
    return 'De pc op kantoor lijkt uit te staan of het programma draait niet. De vraag blijft een uur in de wachtrij staan.'
  }
  if (s.voor && s.voor > 0) return `In de wachtrij: er ${s.voor === 1 ? 'staat 1 vraag' : `staan ${s.voor} vragen`} vóór je.`
  return 'Doorgegeven aan de eigen AI…'
}
